import React, { useState, useEffect } from 'react';
import { Logo, Icon } from '../brand/Primitives';
import { FAKE_RESTAURANTS } from './fakeData';

const SCENES = ['discovery', 'detail', 'chat', 'outro'];
const DURATIONS = [6500, 6000, 8500, 4500];

export default function DemoModal({ open, onClose, onGetStarted }) {
  const [scene, setScene] = useState(0);
  const [playing, setPlaying] = useState(true);

  useEffect(() => {
    if (!open || !playing) return;
    const t = setTimeout(() => setScene((s) => (s + 1) % SCENES.length), DURATIONS[scene]);
    return () => clearTimeout(t);
  }, [scene, open, playing]);

  useEffect(() => {
    if (open) {
      setScene(0);
      setPlaying(true);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const captions = [
    { eyebrow: 'Step 1 of 3', title: 'Discover', body: 'Swipe through restaurants that fit your diet. Right to save, left to skip — we learn what works for you.' },
    { eyebrow: 'Step 2 of 3', title: 'Preview', body: "Tap any place to see what's safe and what to avoid — before you even sit down." },
    { eyebrow: 'Step 3 of 3', title: 'Chat', body: 'Ask the menu anything. Get answers in seconds, trained on the real kitchen.' },
    { eyebrow: "That's it.", title: 'Ready to try it?', body: 'Start discovering places that fit the way you eat.' },
  ];

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: 'rgba(10, 37, 64, 0.78)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 32,
        animation: 'cwmFadeIn 220ms ease-out',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(1040px, 100%)',
          background: '#fff',
          borderRadius: 28,
          overflow: 'hidden',
          display: 'grid',
          gridTemplateColumns: '1fr 1.1fr',
          boxShadow: '0 40px 120px rgba(0,0,0,0.5)',
          position: 'relative',
          animation: 'cwmAlertIn 300ms cubic-bezier(.2,.9,.3,1.1)',
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 5,
            width: 36,
            height: 36,
            borderRadius: 999,
            border: 'none',
            background: 'rgba(10,37,64,0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'var(--cwm-ink)',
          }}
        >
          <Icon name="close" size={20} />
        </button>

        <div
          style={{
            background: 'linear-gradient(160deg, #0A2540 0%, #123E6B 100%)',
            color: '#fff',
            padding: '40px 40px 32px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            minHeight: 620,
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
              <Logo variant="mark-white" height={28} />
              <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', opacity: 0.75 }}>
                Product tour
              </div>
            </div>

            <div style={{ display: 'flex', gap: 6, marginBottom: 26 }}>
              {SCENES.map((_, i) => (
                <div
                  key={i}
                  onClick={() => setScene(i)}
                  style={{
                    height: 3,
                    flex: 1,
                    borderRadius: 999,
                    background: 'rgba(255,255,255,0.2)',
                    overflow: 'hidden',
                    cursor: 'pointer',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      background: '#fff',
                      width: i < scene ? '100%' : i === scene ? '100%' : '0%',
                      transition: i === scene ? `width ${DURATIONS[i]}ms linear` : 'none',
                    }}
                  />
                </div>
              ))}
            </div>

            <div key={scene} style={{ animation: 'cwmFadeIn 400ms ease-out' }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: '#8FC7FF',
                  marginBottom: 10,
                }}
              >
                {captions[scene].eyebrow}
              </div>
              <h2 style={{ fontSize: 38, fontWeight: 900, letterSpacing: '-0.02em', margin: '0 0 14px', lineHeight: 1.08 }}>
                {captions[scene].title}
              </h2>
              <p style={{ fontSize: 16, lineHeight: 1.55, opacity: 0.88, margin: 0, fontWeight: 500, maxWidth: 360 }}>
                {captions[scene].body}
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 32 }}>
            <button
              onClick={() => setPlaying((p) => !p)}
              style={{
                width: 40,
                height: 40,
                borderRadius: 999,
                border: '1.5px solid rgba(255,255,255,0.3)',
                background: 'rgba(255,255,255,0.08)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <Icon name={playing ? 'pause' : 'play_arrow'} size={22} />
            </button>
            <button
              onClick={() => setScene((s) => (s + 1) % SCENES.length)}
              style={{
                padding: '10px 18px',
                borderRadius: 999,
                border: '1.5px solid rgba(255,255,255,0.3)',
                background: 'rgba(255,255,255,0.08)',
                color: '#fff',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              Next <Icon name="chevron_right" size={16} />
            </button>
            <div style={{ flex: 1 }} />
            <div style={{ fontSize: 12, fontWeight: 700, opacity: 0.6 }}>
              {scene + 1} / {SCENES.length}
            </div>
          </div>
        </div>

        <div
          style={{
            background: 'linear-gradient(180deg, #E8F1FB 0%, #C9DEF3 100%)',
            padding: '40px 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            minHeight: 620,
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 30,
              left: 30,
              width: 120,
              height: 120,
              borderRadius: 999,
              background: 'radial-gradient(closest-side, rgba(30,155,255,0.3), rgba(30,155,255,0))',
            }}
          />
          <div
            style={{
              position: 'absolute',
              bottom: 30,
              right: 30,
              width: 160,
              height: 160,
              borderRadius: 999,
              background: 'radial-gradient(closest-side, rgba(10,37,64,0.18), rgba(10,37,64,0))',
            }}
          />

          <PhoneFrame>
            {scene === 0 && <DemoDiscovery />}
            {scene === 1 && <DemoDetail />}
            {scene === 2 && <DemoChat />}
            {scene === 3 && <DemoOutro onClose={onClose} onGetStarted={onGetStarted} />}
          </PhoneFrame>
        </div>
      </div>
    </div>
  );
}

function PhoneFrame({ children }) {
  return (
    <div
      style={{
        width: 300,
        height: 620,
        background: '#0A2540',
        borderRadius: 44,
        padding: 8,
        boxShadow: '0 30px 70px rgba(10,37,64,0.45), inset 0 0 0 2px rgba(255,255,255,0.06)',
        position: 'relative',
      }}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          background: 'linear-gradient(180deg, #0A2540 0%, #1E9BFF 100%)',
          borderRadius: 36,
          overflow: 'hidden',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 110,
            height: 22,
            background: '#0A2540',
            borderBottomLeftRadius: 14,
            borderBottomRightRadius: 14,
            zIndex: 10,
          }}
        />
        {children}
      </div>
    </div>
  );
}

function DemoDiscovery() {
  const [idx, setIdx] = useState(0);
  const [zone, setZone] = useState(null);
  const [dragX, setDragX] = useState(0);
  const [committing, setCommitting] = useState(null);

  const deck = FAKE_RESTAURANTS;
  const n = deck.length;
  const at = (o) => deck[((idx + o) % n + n) % n];

  useEffect(() => {
    const steps = [
      { at: 900, type: 'zone', v: 'right', drag: 60 },
      { at: 1600, type: 'commit', dir: 'save' },
      { at: 2600, type: 'zone', v: 'left', drag: -60 },
      { at: 3400, type: 'commit', dir: 'skip' },
      { at: 4400, type: 'zone', v: 'right', drag: 60 },
      { at: 5100, type: 'commit', dir: 'save' },
    ];
    const timers = [];
    steps.forEach((s) => {
      timers.push(
        setTimeout(() => {
          if (s.type === 'zone') {
            setZone(s.v);
            setDragX(s.drag ?? 0);
          } else if (s.type === 'commit') {
            setCommitting(s.dir);
            setTimeout(() => {
              setIdx((i) => (i + 1) % n);
              setZone(null);
              setDragX(0);
              setCommitting(null);
            }, 280);
          }
        }, s.at),
      );
    });
    return () => timers.forEach(clearTimeout);
  }, [n]);

  const flying = committing ? (committing === 'save' ? 260 : -260) : 0;
  const flyRot = committing ? (committing === 'save' ? 18 : -18) : 0;
  const flyOp = committing ? 0 : 1;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', paddingTop: 26, color: '#fff', overflow: 'hidden', position: 'relative' }}>
      <div style={{ padding: '6px 14px 6px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <div
          style={{
            width: 22,
            height: 22,
            borderRadius: 7,
            background: 'linear-gradient(180deg,#1FA8FF,#0F7FE8)',
            boxShadow: '0 4px 10px rgba(15,127,232,0.5), inset 0 1px 0 rgba(255,255,255,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Logo variant="mark-white" height={12} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 0.95 }}>
          <div style={{ fontSize: 5.5, fontWeight: 800, letterSpacing: '0.2em', color: 'rgba(255,255,255,0.7)' }}>CHAT WITH</div>
          <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: '-0.02em' }}>Menu</div>
        </div>
        <div style={{ flex: 1 }} />
        <div
          style={{
            fontSize: 7,
            fontWeight: 800,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.8)',
            padding: '3px 7px',
            borderRadius: 999,
            background: 'rgba(255,255,255,0.18)',
            backdropFilter: 'blur(6px)',
          }}
        >
          NYC
        </div>
      </div>

      <div style={{ display: 'flex', gap: 4, padding: '0 14px 6px', flexWrap: 'nowrap' }}>
        {[{ l: 'Vegan', tone: 1 }, { l: 'Nut-free', tone: 0 }, { l: 'GF', tone: 4 }].map((c) => (
          <div
            key={c.l}
            style={{
              fontSize: 8.5,
              fontWeight: 800,
              padding: '3px 7px',
              borderRadius: 999,
              background: `var(--cwm-grp-${c.tone}-bg)`,
              color: `var(--cwm-grp-${c.tone}-fg)`,
              border: `1px solid var(--cwm-grp-${c.tone}-br)`,
              whiteSpace: 'nowrap',
              boxShadow: '0 0 0 2px currentColor inset',
            }}
          >
            ✓ {c.l}
          </div>
        ))}
      </div>

      <div
        style={{
          position: 'relative',
          flex: 1,
          margin: '0 -8px',
          background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.22) 18%, rgba(255,255,255,0.22) 82%, rgba(255,255,255,0) 100%)',
          padding: '10px 0 6px',
          overflowX: 'clip',
          overflowY: 'visible',
        }}
      >
        <div style={{ position: 'relative', height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', perspective: 1000 }}>
          <div style={{ position: 'absolute', transform: 'translateX(-150px) scale(0.58)', opacity: 0.55, zIndex: 1, pointerEvents: 'none', filter: 'blur(0.3px)' }}>
            <MiniCard r={at(-2)} peek />
          </div>
          <div style={{ position: 'absolute', transform: 'translateX(-95px) scale(0.72)', opacity: 0.82, zIndex: 2, pointerEvents: 'none' }}>
            <MiniCard r={at(-1)} peek />
          </div>
          <div style={{ position: 'absolute', transform: 'translateX(95px) scale(0.72)', opacity: 0.82, zIndex: 2, pointerEvents: 'none' }}>
            <MiniCard r={at(1)} peek />
          </div>
          <div style={{ position: 'absolute', transform: 'translateX(150px) scale(0.58)', opacity: 0.55, zIndex: 1, pointerEvents: 'none', filter: 'blur(0.3px)' }}>
            <MiniCard r={at(2)} peek />
          </div>

          <div
            style={{
              position: 'absolute',
              zIndex: 10,
              transform: committing
                ? `translateX(${flying}px) rotate(${flyRot}deg)`
                : zone === 'left'
                ? `translateX(${dragX}px) rotate(-2deg) translateX(-6px)`
                : zone === 'right'
                ? `translateX(${dragX}px) rotate(2deg) translateX(6px)`
                : 'translateX(0) rotate(0deg)',
              opacity: flyOp,
              transition: committing
                ? 'transform 280ms cubic-bezier(.4,0,.4,1), opacity 280ms ease'
                : 'transform 220ms cubic-bezier(.2,.8,.2,1)',
            }}
          >
            <MiniCard r={at(0)} swipeZone={zone} focused />
          </div>
        </div>

        <div style={{ width: '72%', height: 3, margin: '4px auto 0', borderRadius: 999, background: 'rgba(13,71,161,0.18)', overflow: 'hidden' }}>
          <div
            style={{
              height: '100%',
              width: `${((idx + 1) / n) * 100}%`,
              background: 'linear-gradient(90deg,#10B981 0%,#34D399 100%)',
              boxShadow: '0 0 6px rgba(16,185,129,0.55)',
              borderRadius: 999,
              transition: 'width 260ms cubic-bezier(.2,.8,.2,1)',
            }}
          />
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '6px 0 12px' }}>
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: 999,
            background: '#fff',
            color: 'var(--cwm-blue-deep)',
            border: '1.5px solid rgba(255,255,255,0.9)',
            boxShadow: '0 4px 12px rgba(13,71,161,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 14,
            fontWeight: 900,
            lineHeight: 1,
          }}
        >
          ←
        </div>
        <div
          style={{
            fontSize: 9,
            fontWeight: 800,
            background: 'rgba(255,255,255,0.85)',
            color: 'var(--cwm-ink)',
            padding: '5px 10px',
            borderRadius: 999,
            border: '1px solid rgba(255,255,255,0.9)',
            boxShadow: '0 2px 6px rgba(13,71,161,0.15)',
            backdropFilter: 'blur(6px)',
            letterSpacing: '0.01em',
          }}
        >
          Card {idx + 1} of {n}
        </div>
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: 999,
            background: '#fff',
            color: 'var(--cwm-blue-deep)',
            border: '1.5px solid rgba(255,255,255,0.9)',
            boxShadow: '0 4px 12px rgba(13,71,161,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 14,
            fontWeight: 900,
            lineHeight: 1,
          }}
        >
          →
        </div>
      </div>
    </div>
  );
}

function MiniCard({ r, peek = false, focused = false, swipeZone = null }) {
  const glow =
    swipeZone === 'left'
      ? '0 0 0 4px rgba(220,60,60,0.30), 0 14px 34px rgba(220,60,60,0.32)'
      : swipeZone === 'right'
      ? '0 0 0 4px rgba(46,139,87,0.30), 0 14px 34px rgba(46,139,87,0.32)'
      : '0 14px 28px rgba(10,37,64,0.35)';
  const rating = r.rating || 4.6;
  const reviews = r.reviews || 412;
  const locLine = r.city || 'Brooklyn, NY';
  const chainOrCat = (r.cuisine || 'ITALIAN').toUpperCase();
  const friendsText = r.friends || 'Alex, Priya';
  const hoursText = r.hoursToday || '11:30a – 10:00p';

  return (
    <div
      style={{
        width: 180,
        height: 300,
        background: '#fff',
        borderRadius: 14,
        overflow: 'hidden',
        boxShadow: glow,
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid rgba(255,255,255,0.9)',
        position: 'relative',
        transition: 'box-shadow 220ms ease',
      }}
    >
      {swipeZone && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 1,
            background: swipeZone === 'left' ? 'linear-gradient(to right, rgba(220,60,60,0.08), transparent 40%)' : 'linear-gradient(to left, rgba(46,139,87,0.08), transparent 40%)',
            borderRadius: 14,
          }}
        />
      )}
      {focused && swipeZone === 'left' && (
        <div
          style={{
            position: 'absolute',
            top: 10,
            left: 10,
            zIndex: 6,
            padding: '3px 7px',
            borderRadius: 999,
            background: '#fff',
            color: '#C0392B',
            border: '1.5px solid #C0392B',
            fontSize: 8.5,
            fontWeight: 800,
            letterSpacing: '0.02em',
          }}
        >
          Skip
        </div>
      )}
      {focused && swipeZone === 'right' && (
        <div
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 6,
            padding: '3px 7px',
            borderRadius: 999,
            background: '#fff',
            color: '#16803C',
            border: '1.5px solid #16803C',
            fontSize: 8.5,
            fontWeight: 800,
            letterSpacing: '0.02em',
          }}
        >
          Save
        </div>
      )}

      <div
        style={{
          height: 110,
          backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0) 60%, rgba(0,0,0,0.35) 100%), url(${r.photo})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 6,
            left: 6,
            background: 'rgba(255,255,255,0.96)',
            color: r.open ? '#0F7A3A' : '#C0392B',
            padding: '2px 6px 2px 5px',
            borderRadius: 999,
            fontSize: 7.5,
            fontWeight: 800,
            letterSpacing: '0.03em',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 3,
            boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
          }}
        >
          <span style={{ width: 5, height: 5, borderRadius: 999, background: 'currentColor', boxShadow: r.open ? '0 0 0 2px rgba(22,128,60,0.18)' : 'none' }} />
          {r.open ? 'Open Now' : 'Closed Now'}
        </div>
        <div
          style={{
            position: 'absolute',
            top: 6,
            right: 6,
            width: 24,
            height: 24,
            borderRadius: 999,
            background: 'rgba(255,255,255,0.95)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 6px rgba(220,50,60,0.28)',
            color: '#DC3240',
          }}
        >
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
        </div>
        {focused && (
          <div
            style={{
              position: 'absolute',
              bottom: 6,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 3,
              padding: '3px 6px',
              borderRadius: 999,
              background: 'rgba(10,37,64,0.4)',
              backdropFilter: 'blur(4px)',
            }}
          >
            <span style={{ width: 12, height: 4, borderRadius: 2, background: '#fff' }} />
            <span style={{ width: 4, height: 4, borderRadius: 999, background: 'rgba(255,255,255,0.55)' }} />
            <span style={{ width: 4, height: 4, borderRadius: 999, background: 'rgba(255,255,255,0.55)' }} />
          </div>
        )}
      </div>

      <div style={{ padding: '8px 10px 9px', flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 2 }}>
        {focused && (
          <div style={{ marginTop: -18, marginBottom: 3 }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '3px 7px',
                borderRadius: 999,
                background: '#fff',
                border: '1.2px solid #0D47A1',
                color: '#0D47A1',
                fontSize: 7.5,
                fontWeight: 800,
                boxShadow: '0 2px 6px rgba(13,71,161,0.15)',
                whiteSpace: 'nowrap',
              }}
            >
              {friendsText} liked this
            </span>
          </div>
        )}

        <div
          style={{
            fontSize: 7.5,
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: '#6B7280',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {chainOrCat}
        </div>
        <div
          style={{
            fontSize: 12.5,
            fontWeight: 900,
            color: '#0A2540',
            letterSpacing: '-0.01em',
            lineHeight: 1.15,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            marginTop: 1,
          }}
        >
          {r.name}
        </div>

        <div style={{ fontSize: 9, fontWeight: 600, color: '#6B7280', display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 1 }}>
          <svg viewBox="0 0 24 24" width="9" height="9" fill="none" stroke="#0D47A1" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          {locLine}
        </div>

        <div style={{ fontSize: 9, display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 1, color: '#0A2540' }}>
          <span style={{ color: '#F5A623', letterSpacing: '-1px' }}>★★★★★</span>
          <strong style={{ fontWeight: 900 }}>{rating}</strong>
          <span style={{ color: '#6B7280' }}>({reviews})</span>
        </div>

        {focused && (
          <div style={{ fontSize: 8.5, fontWeight: 700, color: '#4B5563', display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 1 }}>
            <svg viewBox="0 0 24 24" width="8" height="8" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            Today · {hoursText}
          </div>
        )}

        <div style={{ display: 'flex', gap: 3, marginTop: 4, minHeight: 14, overflow: 'hidden' }}>
          {r.safe.slice(0, 2).map((s) => (
            <span
              key={s.label}
              style={{
                fontSize: 7,
                fontWeight: 800,
                padding: '2px 5px',
                borderRadius: 999,
                background: '#E6FBEF',
                color: '#0F7A3A',
                border: '1px solid #A6E8C2',
                whiteSpace: 'nowrap',
              }}
            >
              {s.label}
            </span>
          ))}
          {r.warn.slice(0, 1).map((w) => (
            <span
              key={w.label}
              style={{
                fontSize: 7,
                fontWeight: 800,
                padding: '2px 5px',
                borderRadius: 999,
                background: '#FFE9E9',
                color: '#9F1818',
                border: '1px solid #F4B6B6',
                whiteSpace: 'nowrap',
              }}
            >
              {w.label}
            </span>
          ))}
        </div>

        <div style={{ flex: 1 }} />

        {focused && (
          <button
            style={{
              width: '100%',
              height: 26,
              marginTop: 4,
              borderRadius: 999,
              border: 'none',
              background: 'linear-gradient(180deg,#1E9BFF,#0F7FE8)',
              color: '#fff',
              fontWeight: 800,
              fontSize: 9.5,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              boxShadow: '0 4px 10px rgba(30,155,255,0.4)',
            }}
          >
            <span
              style={{
                width: 13,
                height: 13,
                borderRadius: 999,
                background: 'rgba(255,255,255,0.22)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Logo variant="mark-white" height={9} />
            </span>
            Chat With Menu
          </button>
        )}

        {focused && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 3, marginTop: 4 }}>
            {[{ icon: 'place', l: 'Directions' }, { icon: 'language', l: 'Website' }, { icon: 'share', l: 'Share' }].map((b) => (
              <div
                key={b.l}
                style={{
                  height: 18,
                  borderRadius: 999,
                  background: '#fff',
                  color: '#0D47A1',
                  border: '1px solid #BBDEFB',
                  fontSize: 7,
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                }}
              >
                <Icon name={b.icon} size={8} color="#0D47A1" />
                {b.l}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DemoDetail() {
  const r = FAKE_RESTAURANTS[0];
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#F5F8FC', paddingTop: 22, overflow: 'hidden' }}>
      <div
        style={{
          height: 150,
          backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.45) 100%), url(${r.photo})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 8,
            left: 8,
            background: 'rgba(255,255,255,0.95)',
            padding: '4px 8px 4px 6px',
            borderRadius: 999,
            display: 'flex',
            alignItems: 'center',
            gap: 3,
            fontSize: 10,
            fontWeight: 800,
            color: 'var(--cwm-ink)',
          }}
        >
          <Icon name="chevron_left" size={12} /> Back
        </div>
        <div style={{ position: 'absolute', bottom: 10, left: 12, display: 'flex', gap: 5 }}>
          <div
            style={{
              background: '#E6FBEF',
              color: '#0F7A3A',
              padding: '3px 8px',
              borderRadius: 999,
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              border: '1px solid #A6E8C2',
            }}
          >
            <span style={{ width: 4, height: 4, borderRadius: 999, background: 'currentColor' }} />
            Open · until 10pm
          </div>
          <div
            style={{
              background: 'rgba(255,255,255,0.96)',
              padding: '3px 8px',
              borderRadius: 999,
              fontSize: 10,
              fontWeight: 900,
              color: 'var(--cwm-ink)',
              display: 'flex',
              alignItems: 'center',
              gap: 3,
            }}
          >
            <Icon name="star" size={10} color="var(--cwm-star)" /> {r.rating}
          </div>
        </div>
      </div>

      <div style={{ padding: '10px 14px', flex: 1, overflow: 'hidden' }}>
        <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--cwm-gray-500)' }}>
          {r.cuisine} · {r.price} · {r.distance}
        </div>
        <div style={{ fontSize: 17, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '-0.01em', marginTop: 2 }}>{r.name}</div>
        <div style={{ fontSize: 10.5, fontWeight: 500, color: 'var(--cwm-gray-500)', marginTop: 3, lineHeight: 1.4 }}>
          Handmade pasta, wood-fired pizza, seasonal menu.
        </div>

        <div style={{ marginTop: 10, background: 'var(--cwm-gray-50)', borderRadius: 10, padding: '8px 10px', animation: 'cwmFadeIn 400ms 200ms both ease-out' }}>
          <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--cwm-gray-500)' }}>Safe for you</div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 5 }}>
            {r.safe.map((s) => (
              <span
                key={s.label}
                style={{
                  fontSize: 8.5,
                  fontWeight: 800,
                  padding: '2px 6px',
                  borderRadius: 999,
                  background: `var(--cwm-grp-${s.tone}-bg)`,
                  color: `var(--cwm-grp-${s.tone}-fg)`,
                  border: `1px solid var(--cwm-grp-${s.tone}-br)`,
                }}
              >
                {s.label}
              </span>
            ))}
          </div>
        </div>

        <div style={{ marginTop: 6, background: 'var(--cwm-gray-50)', borderRadius: 10, padding: '8px 10px', animation: 'cwmFadeIn 400ms 360ms both ease-out' }}>
          <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--cwm-gray-500)' }}>Heads up</div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 5 }}>
            {r.warn.map((w) => (
              <span
                key={w.label}
                style={{
                  fontSize: 8.5,
                  fontWeight: 800,
                  padding: '2px 6px',
                  borderRadius: 999,
                  background: `var(--cwm-grp-${w.tone}-bg)`,
                  color: `var(--cwm-grp-${w.tone}-fg)`,
                  border: `1px solid var(--cwm-grp-${w.tone}-br)`,
                }}
              >
                {w.label}
              </span>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
          <button
            style={{
              flex: 1,
              padding: '9px 0',
              borderRadius: 999,
              background: 'linear-gradient(180deg,#1E9BFF,#0F7FE8)',
              color: '#fff',
              fontWeight: 800,
              fontSize: 11,
              border: 'none',
              boxShadow: '0 6px 16px rgba(30,155,255,0.4)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              animation: 'cwmPulse 1400ms 600ms infinite',
            }}
          >
            <Icon name="chat_bubble" size={12} color="#fff" /> Chat with menu
          </button>
          <button
            style={{
              padding: '9px 12px',
              borderRadius: 999,
              background: '#fff',
              color: 'var(--cwm-blue-deep)',
              fontWeight: 800,
              fontSize: 11,
              border: '1.5px solid var(--cwm-gray-200)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <Icon name="place" size={12} color="var(--cwm-blue-deep)" />
          </button>
        </div>
      </div>
    </div>
  );
}

const DEMO_CHAT = [
  { role: 'ai', text: "Hi! I'm chatting with Giulia's Trattoria's menu. Tell me what you can't eat, or what you're craving.", at: 0 },
  { role: 'user', text: "I'm vegan with a nut allergy. What's safe?", at: 1500 },
  {
    role: 'ai',
    text: 'The Focaccia is vegan and nut-free. The kitchen can also do tagliatelle with olive oil and seasonal veg — fully vegan.',
    tags: [{ label: 'Vegan', tone: 1 }, { label: 'Nut-Free', tone: 0 }],
    at: 3000,
  },
  { role: 'user', text: 'Anything for dessert?', at: 5000 },
  {
    role: 'ai',
    text: 'Skip the Torta Nocciola (hazelnuts). The sorbet is vegan and made in a nut-free station.',
    tags: [{ label: 'Contains nuts', tone: 2 }],
    at: 6500,
  },
];

function DemoChat() {
  const [visibleN, setVisibleN] = useState(0);
  const [typing, setTyping] = useState(false);

  useEffect(() => {
    setVisibleN(0);
    const timers = [];
    DEMO_CHAT.forEach((m, i) => {
      if (m.role === 'ai' && i > 0) {
        timers.push(setTimeout(() => setTyping(true), Math.max(0, m.at - 500)));
      }
      timers.push(
        setTimeout(() => {
          setTyping(false);
          setVisibleN((n) => Math.max(n, i + 1));
        }, m.at),
      );
    });
    return () => timers.forEach(clearTimeout);
  }, []);

  const shown = DEMO_CHAT.slice(0, visibleN);
  const r = FAKE_RESTAURANTS[0];

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#F5F8FC', paddingTop: 22, overflow: 'hidden' }}>
      <div
        style={{
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(180deg, rgba(10,37,64,0.98), rgba(18,62,107,0.98))',
          color: '#fff',
        }}
      >
        <div
          style={{
            background: 'rgba(255,255,255,0.18)',
            padding: '3px 7px 3px 5px',
            borderRadius: 999,
            fontSize: 9,
            fontWeight: 800,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Icon name="chevron_left" size={12} color="#fff" /> Back
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 8, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', opacity: 0.75 }}>Chatting with</div>
          <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: '-0.01em' }}>{r.name}</div>
        </div>
        <div style={{ width: 36 }} />
      </div>

      <div style={{ flex: 1, overflow: 'hidden', padding: '12px 12px 4px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {shown.map((m, i) => (
          <DemoBubble key={i} {...m} fresh={i === shown.length - 1} />
        ))}
        {typing && <DemoTyping />}
      </div>

      <div style={{ padding: '6px 10px 10px', borderTop: '1px solid var(--cwm-gray-200)', background: '#fff' }}>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
          {["I'm gluten-free", "What's vegan?"].map((s) => (
            <div
              key={s}
              style={{
                border: '1.5px solid var(--cwm-blue-light)',
                background: '#fff',
                color: 'var(--cwm-blue-deep)',
                padding: '3px 8px',
                borderRadius: 999,
                fontSize: 9,
                fontWeight: 800,
              }}
            >
              {s}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 5 }}>
          <div
            style={{
              flex: 1,
              padding: '7px 12px',
              borderRadius: 999,
              border: '1.5px solid var(--cwm-blue-light)',
              fontSize: 10,
              fontWeight: 500,
              color: 'var(--cwm-gray-500)',
              background: '#fff',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            Ask about any menu item…
          </div>
          <div
            style={{
              padding: '7px 12px',
              borderRadius: 999,
              background: 'linear-gradient(180deg,#1E9BFF,#0F7FE8)',
              color: '#fff',
              fontSize: 10,
              fontWeight: 800,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
            }}
          >
            <Icon name="send" size={11} color="#fff" /> Send
          </div>
        </div>
      </div>
    </div>
  );
}

function DemoBubble({ role, text, tags, fresh }) {
  const isUser = role === 'user';
  if (isUser) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', animation: fresh ? 'cwmPop 280ms ease-out both' : 'none' }}>
        <div
          style={{
            background: 'linear-gradient(180deg,#1E9BFF,#0F7FE8)',
            color: '#fff',
            padding: '7px 10px',
            borderRadius: '14px 14px 3px 14px',
            maxWidth: '78%',
            fontSize: 10.5,
            fontWeight: 600,
            lineHeight: 1.4,
            boxShadow: '0 4px 10px rgba(30,155,255,0.3)',
          }}
        >
          {text}
        </div>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', gap: 5, alignItems: 'flex-end', animation: fresh ? 'cwmPop 280ms ease-out both' : 'none' }}>
      <div
        style={{
          width: 22,
          height: 22,
          flex: '0 0 22px',
          borderRadius: 999,
          background: 'var(--cwm-blue)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 900,
          fontSize: 8,
          letterSpacing: '0.04em',
        }}
      >
        AI
      </div>
      <div
        style={{
          background: '#fff',
          color: 'var(--cwm-ink)',
          padding: '7px 10px',
          borderRadius: '14px 14px 14px 3px',
          maxWidth: '78%',
          fontSize: 10.5,
          fontWeight: 500,
          lineHeight: 1.45,
          border: '1.5px solid var(--cwm-gray-200)',
        }}
      >
        {text}
        {tags && (
          <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginTop: 5 }}>
            {tags.map((t) => (
              <span
                key={t.label}
                style={{
                  fontSize: 8,
                  fontWeight: 800,
                  padding: '2px 5px',
                  borderRadius: 999,
                  background: `var(--cwm-grp-${t.tone}-bg)`,
                  color: `var(--cwm-grp-${t.tone}-fg)`,
                  border: `1px solid var(--cwm-grp-${t.tone}-br)`,
                }}
              >
                {t.label}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DemoTyping() {
  return (
    <div style={{ display: 'flex', gap: 5, alignItems: 'flex-end' }}>
      <div
        style={{
          width: 22,
          height: 22,
          flex: '0 0 22px',
          borderRadius: 999,
          background: 'var(--cwm-blue)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 900,
          fontSize: 8,
        }}
      >
        AI
      </div>
      <div
        style={{
          background: '#fff',
          padding: '8px 12px',
          borderRadius: '14px 14px 14px 3px',
          border: '1.5px solid var(--cwm-gray-200)',
          display: 'flex',
          gap: 3,
        }}
      >
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            style={{
              width: 5,
              height: 5,
              borderRadius: 999,
              background: 'var(--cwm-blue-deep)',
              animation: `cwmDot 1.1s ${i * 0.15}s infinite ease-in-out`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

function DemoOutro({ onClose, onGetStarted }) {
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(180deg,#0A2540 0%,#1E9BFF 100%)',
        color: '#fff',
        padding: '36px 22px 22px',
        paddingTop: 46,
        textAlign: 'center',
      }}
    >
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Logo variant="mark-white" height={64} />
        <h2 style={{ fontSize: 22, fontWeight: 900, letterSpacing: '-0.01em', margin: '16px 0 8px', lineHeight: 1.15 }}>
          Eat well.
          <br />
          Skip the worry.
        </h2>
        <p style={{ fontSize: 12, opacity: 0.85, margin: 0, lineHeight: 1.5, fontWeight: 500 }}>Free to try. No card needed.</p>
      </div>
      <button
        onClick={() => {
          onClose();
          onGetStarted && onGetStarted();
        }}
        style={{
          width: '100%',
          padding: '11px 0',
          borderRadius: 999,
          background: '#fff',
          color: 'var(--cwm-blue-deep)',
          fontWeight: 900,
          fontSize: 13,
          border: 'none',
          cursor: 'pointer',
          boxShadow: '0 8px 20px rgba(0,0,0,0.25)',
        }}
      >
        Start discovering
      </button>
    </div>
  );
}
